import {
  ChevronDownIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useId } from 'react';

import { formatCents, leadTimeLabel, sanitizeAmountInput } from '../format';
import { SurgeDraftState } from '../hooks/useSurgeDraft';

import '../paySurge.css';

type Props = {
  state: SurgeDraftState;
  maxCents: number;
  leadTimeOptions: number[];
  /** What the off switch means, in the scope being edited. */
  offDescription: string;
  /**
   * Lead times whose tier has already fired or been skipped on THIS job
   * (GRAVY-10860). Their amount is settled: the sweep will not apply a new one,
   * so the field is read-only rather than accepting a number that would never
   * be paid. Their lead time is locked and they have no trash either: the
   * server keys tiers by lead time, so moving or dropping one deletes the fired
   * tier and a moved one comes back as a NEW pending tier that fires again.
   * Empty on the client Rates tab, which has no per-job state.
   */
  settledLeadTimes?: number[];
  disabled?: boolean;
};

const ON_DESCRIPTION =
  'Automatically raises pay in steps as an unfilled shift gets closer to start — each tier holds until the next one begins.';

/**
 * The surge pay switch and its tier list (GRAVY-10443) — one component behind
 * both the client Rates tab (Figma 40551:178777) and Manage surge pay
 * (38664:8957), because they are the same schedule at two scopes.
 *
 * A lead time already used by another row is disabled in each dropdown, so two
 * tiers can never share one. The first row has no trash: a schedule that is on
 * keeps at least one tier (Product, Q3).
 */
const SurgeTierEditor = ({
  state,
  maxCents,
  leadTimeOptions,
  offDescription,
  settledLeadTimes = [],
  disabled = false,
}: Props) => {
  const id = useId();
  const { draft, errors } = state;
  const locked = disabled || !draft.enabled;

  return (
    <section className="paysurge-editor" data-testid="surgeEditor">
      <div className="paysurge-switchrow">
        <div className="paysurge-switchtext">
          <span className="paysurge-title" id={`${id}-title`}>
            Surge pay
          </span>
          <span className="paysurge-help">
            {draft.enabled ? ON_DESCRIPTION : offDescription}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={draft.enabled}
          aria-labelledby={`${id}-title`}
          className="paysurge-switch"
          data-testid="surgeToggle"
          disabled={disabled}
          onClick={() => state.setEnabled(!draft.enabled)}
        />
      </div>

      <div
        className={`paysurge-tiers${locked ? ' is-locked' : ''}`}
        aria-disabled={locked}
      >
        {draft.tiers.map((tier, index) => {
          const error = errors[index];
          const n = index + 1;
          const settled = settledLeadTimes.includes(tier.leadTimeMinutes);
          return (
            <div key={index} className="paysurge-tier" data-testid="surgeTier">
              <div className="paysurge-tierrow">
                <label className="paysurge-field">
                  <span className="paysurge-label">Lead time</span>
                  <span
                    className={`paysurge-control is-select${error ? ' has-error' : ''}`}
                  >
                    <select
                      aria-label={`Tier ${n} lead time`}
                      disabled={locked || settled}
                      value={tier.leadTimeMinutes}
                      onChange={(e) =>
                        state.updateTier(index, {
                          leadTimeMinutes: Number(e.target.value),
                        })
                      }
                    >
                      {leadTimeOptions.map((minutes) => (
                        <option
                          key={minutes}
                          value={minutes}
                          disabled={draft.tiers.some(
                            (other, i) =>
                              i !== index && other.leadTimeMinutes === minutes,
                          )}
                        >
                          {leadTimeLabel(minutes)}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon aria-hidden="true" />
                  </span>
                </label>

                <label className="paysurge-field">
                  <span className="paysurge-label">Surge rate</span>
                  <span
                    className={`paysurge-control is-money${error ? ' has-error' : ''}`}
                  >
                    <PlusIcon aria-hidden="true" />
                    <input
                      aria-label={`Tier ${n} surge rate`}
                      aria-invalid={Boolean(error)}
                      disabled={locked}
                      readOnly={settled}
                      inputMode="decimal"
                      placeholder="$0.00"
                      value={
                        tier.rate ? `$${tier.rate.replace(/^\$/, '')}` : ''
                      }
                      onChange={(e) =>
                        state.updateTier(index, {
                          rate: sanitizeAmountInput(e.target.value),
                        })
                      }
                    />
                    <span className="paysurge-suffix">per hr</span>
                  </span>
                </label>

                {index > 0 && !settled && (
                  <button
                    type="button"
                    className="paysurge-remove"
                    aria-label={`Remove tier ${n}`}
                    disabled={locked}
                    onClick={() => state.removeTier(index)}
                  >
                    <TrashIcon aria-hidden="true" />
                  </button>
                )}
              </div>
              {error && (
                <p className="paysurge-error" role="alert">
                  <ExclamationCircleIcon aria-hidden="true" />
                  {error}
                </p>
              )}
              {settled && !error && (
                <p
                  className="paysurge-note is-tiernote"
                  data-testid="surgeSettledTier"
                >
                  <InformationCircleIcon aria-hidden="true" />
                  This tier has already passed. Use Override amount to raise pay
                  now.
                </p>
              )}
            </div>
          );
        })}

        {state.canAddTier && (
          <button
            type="button"
            className="paysurge-add"
            disabled={locked}
            onClick={state.addTier}
          >
            <PlusIcon aria-hidden="true" />
            Add another tier
          </button>
        )}
      </div>

      {/* The platform ceiling, stated where the tiers are set (§3.3). A
          per-client cap input was descoped for V1. */}
      <p className="paysurge-note" data-testid="surgeCeilingNote">
        <InformationCircleIcon aria-hidden="true" />
        Surge will never raise pay more than {formatCents(maxCents)}/hr above
        base.
      </p>
    </section>
  );
};

export default SurgeTierEditor;
